import { Button } from '@strata/react-buttons';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { ErrorToast } from '@strata/react-toast-message';
import { getIn, useFormikContext } from 'formik';
import PropTypes from 'prop-types';

import { checkReferralRoute } from 'api/clientAPI';
import { handleError } from 'services/Error';
import {
    optionalParameterHelperText,
    Sections,
    SectionToStepMap,
} from 'services/testReferralHelpers';
import {
    useGetConfiguredProductsForPayerQuery,
    useGetDiagnosesQuery,
    useLazyGetEligibleSuppliersQuery,
    useLazyGetPrescriberDetailsQuery,
} from 'store/api/referenceApi';
import { useTestReferralData } from 'store/testReferral/testReferralSlice';

export const ProductSection = ({ setLoadingText }) => {
    const formik = useFormikContext();
    const { setOpenSection } = useTestReferralData();
    const {
        healthPlan,
        cardholderId,
        patientFirstName,
        memberDOB,
        benefit,
        eligibility,
        prescriber,
        placeOfService,
    } = getIn(formik.values, Sections.PATIENT_AND_PHARMACY);
    const selectedPayer = healthPlan?.payerId;
    const { data: productsList, isFetching: isProductsListFetching } =
        useGetConfiguredProductsForPayerQuery(
            { segmentId: healthPlan?.secondaryData },
            { skip: !healthPlan },
        );
    const { data: diagnoses, isFetching: diagnosesFetching } = useGetDiagnosesQuery();

    const { foundResult: _foundResult, ...productErrors } =
        getIn(formik.errors, Sections.PRODUCT) ?? {};

    const resetResults = async () => {
        await formik.setFieldValue(Sections.RESULTS, undefined);
        await formik.setFieldValue(`${Sections.PRODUCT}.foundResult`, false);
        const { patientAndPharmacy: patientAndPharmacyTouched, product: productTouched } =
            formik.touched;
        formik.setTouched({
            patientAndPharmacy: patientAndPharmacyTouched,
            product: productTouched,
        });
    };

    const [getPrescriberDetails] = useLazyGetPrescriberDetailsQuery();
    const [getEligibleSuppliers] = useLazyGetEligibleSuppliersQuery();

    // eslint-disable-next-line complexity
    const testReferral = async () => {
        try {
            await resetResults();
            const { includePendStart } = getIn(formik.values, Sections.PATIENT_AND_PHARMACY);
            const { product, diagnosis } = getIn(formik.values, Sections.PRODUCT);

            const { isError } = await getPrescriberDetails({ prescriberNpi: prescriber });
            if (isError) {
                ErrorToast({ message: 'Prescriber does not exist' });
                return;
            }

            const { data: eligibleSuppliers, isError: eligibleSuppliersIsError } =
                await getEligibleSuppliers({
                    payerId: selectedPayer,
                    segmentId: healthPlan.secondaryData,
                    memberState: eligibility?.member_state,
                    productServiceOid: product?.oid,
                    benefit: benefit?.value,
                    placeOfService: placeOfService?.secondaryData,
                    isBrand: product?.isBrand,
                    lineOfBusiness: eligibility?.line_of_business,
                    lobSegment: eligibility?.lob_segment,
                });
            if (eligibleSuppliersIsError) {
                // From Acceptance Criteria: "don't run route check if the eligible-suppliers call fails"
                return;
            }

            const routingResults = await checkReferralRoute({
                payerId: selectedPayer,
                params: {
                    referral_id: null,
                    payer_id: selectedPayer,
                    segment_id: healthPlan.secondaryData,
                    member_id: cardholderId,
                    patient_first_name: patientFirstName,
                    patient_dob: memberDOB,
                    product_service_oid: product?.oid,
                    is_brand: product?.isBrand,
                    benefit: benefit?.value,
                    member_benefit_group: eligibility.benefit_group?.trim(),
                    patient_zip: eligibility?.member_zip?.trim(),
                    supplier_npi: null,
                    prescriber_npi: prescriber,
                    prescriber_practice_npi: null,
                    marketplace_participants: (eligibleSuppliers ?? []).map(({ npi }) => npi),
                    place_of_service: placeOfService?.secondaryData,
                    therapeutic_category: product?.therapeuticCategory,
                    dx_oid: diagnosis?.value,
                    check_only: true,
                    include_pend_start: includePendStart,
                },
            });

            await formik.setFieldValue(Sections.RESULTS, routingResults);
            await formik.setFieldValue(`${Sections.PRODUCT}.foundResult`, true);
            setOpenSection(SectionToStepMap[Sections.RESULTS]);
            formik.setFieldTouched(Sections.RESULTS);
        } catch (err) {
            handleError("Can't retrieve test referral results.", err);
        }
    };

    return (
        <>
            <SuggestSingleSelectFormik
                id={`${Sections.PRODUCT}.product`}
                label="Product"
                placeholderText="Select Product"
                isLoading={isProductsListFetching}
                values={productsList?.filter(
                    (product) =>
                        product.benefit ===
                        getIn(formik.values, `${Sections.PATIENT_AND_PHARMACY}.benefit`)?.value,
                )}
                onChange={async (newValue) => {
                    await formik.setFieldValue(`${Sections.PRODUCT}.product`, newValue);
                    await resetResults();
                }}
                required
            />
            <SuggestSingleSelectFormik
                id={`${Sections.PRODUCT}.diagnosis`}
                label="Diagnosis"
                placeholderText="Select Diagnosis"
                helperText={`Selecting a diagnosis is optional. ${optionalParameterHelperText}`}
                values={diagnoses}
                isLoading={diagnosesFetching}
                onChange={async (newValue) => {
                    await formik.setFieldValue(`${Sections.PRODUCT}.diagnosis`, newValue);
                    await resetResults();
                }}
            />
            <Button
                intent={Button.Intent.PRIMARY}
                disabled={Object.keys(productErrors).length > 0}
                label="Select Product"
                onClick={async () => {
                    setLoadingText('Checking Test Referral Route');
                    await testReferral();
                    setLoadingText();
                }}
            />
        </>
    );
};

ProductSection.propTypes = {
    setLoadingText: PropTypes.func.isRequired,
};
